import { create } from "zustand";

import {
  clearStoredVolunteer,
  fetchCurrentVolunteer,
  readStoredVolunteer,
  type VolunteerSessionUser,
  writeStoredVolunteer,
} from "@/Utils/Auth/volunteerAuthSession";
import { loggerService, LogTag } from "@/Utils/LoggerService";

interface VolunteerAuthState {
  user: VolunteerSessionUser | null;
  isAuthenticated: boolean;
  /** Sets the authenticated volunteer and persists it (non-sensitive) for reload. */
  setVolunteer: (user: VolunteerSessionUser) => void;
  /** Clears the volunteer. Tokens are cleared server-side via the logout route. */
  clearSession: () => void;
  /** Revalidates the session against the httpOnly cookie via the volunteer /me. */
  hydrate: () => Promise<void>;
}

const storedVolunteer = readStoredVolunteer();

export const useVolunteerAuthStore = create<VolunteerAuthState>((set) => ({
  user: storedVolunteer,
  isAuthenticated: Boolean(storedVolunteer),

  setVolunteer: (user) => {
    loggerService.info(
      LogTag.AUTH,
      "Volunteer authenticated",
      user.display_name,
    );
    writeStoredVolunteer(user);
    set({ user, isAuthenticated: true });
  },

  clearSession: () => {
    loggerService.info(LogTag.AUTH, "Volunteer logged out");
    clearStoredVolunteer();
    set({ user: null, isAuthenticated: false });
  },

  hydrate: async () => {
    const user = await fetchCurrentVolunteer();

    if (user) {
      writeStoredVolunteer(user);
      set({ user, isAuthenticated: true });
    } else {
      clearStoredVolunteer();
      set({ user: null, isAuthenticated: false });
    }
  },
}));
